import React, { useMemo } from 'react';
import Table from '../../table/Table';
import { ApiSanctionAlert, ApiSanctionEntityType } from '../../api';
import { Link } from 'react-router-dom';
import ScreeningEntityDetails from '../../screening/ScreeningEntityDetails';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import ScreeningMatchesPopover from '../../screening/ScreeningMatchesPopover';
import { formatEnumLabel, isNavigableCellClicked } from '../../common/helper';
import { getValueFromEntityByFieldKey } from '../../screening/helper';
import { useNavigate } from 'react-router';
import { SalvDropdown, SalvProgressCircle, Size } from '@salvapp/ui-components';
import { InternalApiScreeningAlertStatus } from '../../api-internal';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { useAlertStatuses } from '../../screening/alert-status/alertStatusesHelper';
import './ScreeningAlerts.scss';
import { systemAlertStatusCodes } from '../../screening/alert-status/alertStatusesConstants';
import { AlertFilterStatusOperation } from './PersonScreeningAlerts';
import { useMe } from '../../organisation/useMe';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';

interface Props {
  page: number;
  size: number;
  total: number;
  loading: boolean;
  expandable: boolean;
  failedToLoad: boolean;
  loadMorePaginated?: boolean;
  alerts: Array<ApiSanctionAlert>;
  entityType?: ApiSanctionEntityType;
  onUpdatePage?: (page: number) => void;
  selectedOperation?: AlertFilterStatusOperation;
  onUpdateOperation?: (operation: AlertFilterStatusOperation) => void;
  selectedStatuses?: InternalApiScreeningAlertStatus[];
  onUpdateStatuses?: (statuses: InternalApiScreeningAlertStatus[]) => void;
}

export const ScreeningAlerts = ({
  alerts,
  page,
  size,
  total,
  loading,
  expandable,
  onUpdatePage,
  entityType,
  loadMorePaginated,
  failedToLoad,
  selectedOperation,
  onUpdateOperation,
  selectedStatuses,
  onUpdateStatuses,
}: Props) => {
  const { currentOrganisation } = useMe();
  const navigate = useNavigate();
  const navigateToScreeningAlert = (id: number) => navigate(`/screening/alerts/${id}`);
  const [activeStatuses, statuses, deactivatedStatuses] = useAlertStatuses();

  const tableTitle = useMemo(
    () => (
      <>
        {entityType ? formatEnumLabel(entityType + ' screening alerts') : 'Screening alerts'}{' '}
        {!loading && !failedToLoad && (
          <span className='sanction-amount-info'>
            ({total}
            {loadMorePaginated && total > 0 && total % size == 0 && (page + 1) * size == total ? '+' : ''})
          </span>
        )}
      </>
    ),
    [total, loading]
  );

  const statusOptions = useMemo(() => {
    return activeStatuses
      .filter((status) => status.code !== systemAlertStatusCodes.filtered)
      .concat(activeStatuses.filter((status) => status.code === systemAlertStatusCodes.filtered))
      .concat(deactivatedStatuses);
  }, [activeStatuses, deactivatedStatuses]);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderAlerts = () => {
    return alerts.map((alert: ApiSanctionAlert) => (
      <tr
        key={alert.id}
        onClick={(event: any) => isNavigableCellClicked(event) && navigateToScreeningAlert(alert.id)}
        className='clickable-row'
      >
        <td>
          <Link to={`/screening/alerts/${alert.id}`}>{alert.id}</Link>
        </td>
        <td>
          <ScreeningEntityDetails
            entity={alert.entity}
            mappings={attributeMappings.map}
            organisationCurrency={currentOrganisation?.currency}
          />
        </td>
        <td>
          <FormattedDateTime date={alert.createdTime} showTime />
        </td>
        <td className='alert-details'>
          <ScreeningMatchesPopover
            originalInput={getValueFromEntityByFieldKey(alert.fieldKey, alert.entity)}
            hits={alert.matches || []}
            status={alert.status}
          />
        </td>
        <td>{statuses.find((it) => it.id == alert.statusId)?.textName}</td>
      </tr>
    ));
  };

  const updatePage = (page: number) => {
    onUpdatePage && onUpdatePage(page);
  };

  const totalAlertsAmount = () => {
    if (loadMorePaginated && total % size == 0) {
      return total + 1;
    }
    return total;
  };

  const statusToOption = (status: InternalApiScreeningAlertStatus): DropdownOption => {
    return {
      key: status.code,
      label: status.endTime == null ? status.textName : 'Archived: ' + status.textName,
      value: status.code,
    };
  };

  const operationToOption = (operation: AlertFilterStatusOperation): DropdownOption => {
    return {
      key: operation,
      label: operation,
      value: operation,
    };
  };

  return (
    <>
      <Table
        className='screening-alerts-table'
        responsive={false}
        total={totalAlertsAmount()}
        page={page}
        size={size}
        items={alerts}
        fetching={loading}
        fetchPage={updatePage}
        title={tableTitle}
        expandable={expandable}
        expandedByDefault={false}
        hidePageInfo={loadMorePaginated}
        filters={() => {
          return selectedOperation && onUpdateOperation && selectedStatuses && onUpdateStatuses ? (
            <div className='d-flex'>
              <SalvDropdown
                ariaLabel='Filter by status'
                options={Object.values(AlertFilterStatusOperation).map(operationToOption)}
                value={operationToOption(selectedOperation)}
                size={Size.SMALL}
                onChange={(option) => {
                  onUpdateOperation(option[0].value as AlertFilterStatusOperation);
                }}
                clearable={false}
                className='m-r-16'
              />
              <SalvDropdown
                ariaLabel='Filter by status'
                options={statusOptions.map(statusToOption)}
                value={selectedStatuses.map(statusToOption)}
                isMulti={true}
                placeholder='Filter by status'
                size={Size.SMALL}
                clearable={true}
                onChange={(values) => {
                  onUpdateStatuses(values.map((value) => statuses.find((status) => status.code === value.value)!));
                }}
                className='w-100'
              />
            </div>
          ) : undefined;
        }}
        header={() => (
          <tr>
            <th>Alert Id</th>
            <th>Object</th>
            <th>Alert time</th>
            <th>Matches</th>
            <th>Status</th>
          </tr>
        )}
        body={renderAlerts}
        emptyMessage={
          failedToLoad
            ? 'Failed to load alerts. Please try  again.'
            : page > 0
              ? 'No more screening alerts'
              : 'No screening alerts'
        }
      />
    </>
  );
};
export default ScreeningAlerts;
